import React from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Skeleton,
  Divider,
  Stack,
  Chip,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { API_URL } from "./config";
import useAuditView from "./commonfunctions/useAuditView"; // ✅ adjust relative path

// --- token helpers
function decodeJwtPayload(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}
function getToken() {
  return localStorage.getItem("auth");
}
function getCampusFromToken() {
  const t = getToken();
  if (!t) return "";
  const p = decodeJwtPayload(t);
  return p?.campusCode || p?.CampusCode || "";
}
{/*
function useAuditView({ token, feature_key, payload }) {
  React.useEffect(() => {
    if (!token || !feature_key) return;

    // ✅ StrictMode dev double-mount guard (works even across unmount/remount)
    const key = `audit_view_once:${feature_key}:${window.location.pathname}`;
    if (sessionStorage.getItem(key) === "1") return;
    sessionStorage.setItem(key, "1");

    axios.post(
      `${API_URL}/api/audit/log`,
      {
        action: "REPORT_VIEW",
        feature_key,
        route_path: window.location.pathname,
        payload: payload || null,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(() => {});
  }, [token, feature_key]); // (payload intentionally not included)
}
*/}

export default function StudentMarksAnalytics() {
  const token = getToken();
  useAuditView({
  token,
  feature_key: "StudentMarksAnalytics",
  payload: { CampusCode: getCampusFromToken() }, // keep small; don’t send full passMatrix
  });
  const [err, setErr] = React.useState("");

  // ✅ Since Filter section removed: keep only campus + UseLatest (controlled inside Pivot section)
  const [base, setBase] = React.useState({
    CampusCode: getCampusFromToken(),
  });

  // ---------- base data (loaded once) ----------
  const [loadingMatrix, setLoadingMatrix] = React.useState(false);
  const [passMatrix, setPassMatrix] = React.useState([]);

  // ---------- pivot-driven panels ----------
  const [loadingPanels, setLoadingPanels] = React.useState(false);
  const [summary, setSummary] = React.useState(null);
  const [subjects, setSubjects] = React.useState([]);
  const [grades, setGrades] = React.useState([]);
  const [topBottom, setTopBottom] = React.useState(null);

  // AI gist states
  const [loadingAi, setLoadingAi] = React.useState(false);
  const [aiGist, setAiGist] = React.useState(null);
  const [aiMeta, setAiMeta] = React.useState(null);

  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "transparent",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };
  const surfaceSx = { borderRadius: 2, backgroundColor: "#fff", border: `1px solid ${BORDER}` };
  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    //overflowX: "visible",
    //overflowY: "visible",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };
  const sectionInnerPadSx = { pl: 1 };
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    whiteSpace: "nowrap",
  };
  const kpiCardSx = { ...surfaceSx, height: "100%", "& .MuiCardContent-root": { p: 2 } };
  const chipSx = {
    borderRadius: 2,
    backgroundColor: "rgba(25,118,210,0.06)",
    border: "1px solid rgba(25,118,210,0.18)",
    fontWeight: 800,
  };

  // -------------------- PIVOT BUILDER --------------------
  const PIVOT_FIELDS = React.useMemo(
    () => [
      { key: "AcademicYear", label: "AcademicYear" },
      { key: "SubCampusCode", label: "SubCampus" },
      { key: "DegreeCode", label: "Degree" },
      { key: "BranchCode", label: "Branch" },
      { key: "Semester", label: "Semester" },
      { key: "SubjectCode", label: "Subject" },
      { key: "GradeCode", label: "GradeCode" },
      { key: "NoOfBacklogs", label: "NoOfBacklogs (Bucket)" },
      { key: "PercentageBucket", label: "Percentage (Bucket)" },
      { key: "CgpaBucket", label: "CGPA (Bucket)" },
    ],
    []
  );

  const PIVOT_MEASURES = React.useMemo(
    () => [
      { key: "passPercent", label: "Pass %" },
      { key: "passCount", label: "Pass Count" },
      { key: "appearedCount", label: "Appeared Count" },
    ],
    []
  );

  const [pivotCfg, setPivotCfg] = React.useState({
    rows: ["SubCampusCode"],
    cols: ["Semester"],
    filters: ["AcademicYear", "DegreeCode", "BranchCode", "SubCampusCode", "Semester", "SubjectCode", "GradeCode", "NoOfBacklogs", "PercentageBucket", "CgpaBucket"],
    value: "passPercent",
  });

  const [pivotFiltersDraft, setPivotFiltersDraft] = React.useState({
    AcademicYear: [],
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Semester: [],
    SubjectCode: [],
    GradeCode: [],
    NoOfBacklogs: [],
    PercentageBucket: [],
    CgpaBucket: [],
  });

  const [pivotFiltersApplied, setPivotFiltersApplied] = React.useState({
    AcademicYear: [],
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Semester: [],
    SubjectCode: [],
    GradeCode: [],
    NoOfBacklogs: [],
    PercentageBucket: [],
    CgpaBucket: [],
  });

  const BUCKET_OPTIONS = React.useMemo(() => ({
    NoOfBacklogs: ["0", "1", "2", "3", "4", "5+"],
    PercentageBucket: ["90-100", "80-90", "70-80", "60-70", "50-60", "<50"],
    CgpaBucket: ["9-10", "8-9", "7-8", "6-7", "5-6", "<5"],
  }), []);

  // Students list
  const [loadingStudents, setLoadingStudents] = React.useState(false);
  const [students, setStudents] = React.useState([]);
  const [showStudents, setShowStudents] = React.useState(false);

  // ---- helpers
  const getField = (r, k) => (r?.[k] == null ? "" : String(r[k]));

  // Build a lookup: { "R201101": "CDS" } etc.
  // NOTE: This only contains whatever your /analytics/subjects returns for current applied filters.
  const subjectNameByCode = React.useMemo(() => {
    const m = {};
    (subjects || []).forEach((s) => {
      const code = s?.SubjectCode;
      const name = s?.SubjectNameCode || "";
      if (code) m[String(code)] = String(name || "");
    });
    return m;
  }, [subjects]);

  const formatSubjectOption = React.useCallback(
    (subjectCode) => {
      const code = String(subjectCode || "");
      const name = subjectNameByCode[code];
      // show "Name..Code" only if name exists
      return name ? `${name}..${code}` : code;
    },
    [subjectNameByCode]
  );

  const displayDimValue = React.useCallback(
  (key, value) => {
    if (key === "SubjectCode") return formatSubjectOption(value);
    return String(value ?? "");
  },
  [formatSubjectOption]
  );

  // ---- pivot params (multi-select lists)
  // axios serializes arrays as repeated query params: ?DegreeCode=BTECH&DegreeCode=MTECH
  const buildPivotParams = React.useCallback(() => {
    const out = {};
    const pf = pivotFiltersApplied || {};
    Object.keys(pf).forEach((k) => {
      const arr = (pf[k] || []).filter(Boolean);
      if (!arr.length) return;
      out[k] = arr.length === 1 ? arr[0] : arr;
    });
    return out;
  }, [pivotFiltersApplied]);

  // ✅ EVERYTHING is driven by pivot filters now
  const buildEffectiveParams = React.useCallback(() => {
    return {
      CampusCode: base.CampusCode || undefined,
      ...buildPivotParams(),
    };
  }, [base.CampusCode, buildPivotParams]);

  // -------------------- LOAD PASS MATRIX (needed for pivot table) --------------------
  const loadPassMatrix = React.useCallback(async () => {
    if (!token) {
      setErr("Session expired. Please login again.");
      return;
    }
    setErr("");
    setLoadingMatrix(true);
    try {
      // IMPORTANT: matrix should be broad so pivot filters can work client-side
      // (only campus + UseLatest)
      const params = {
        CampusCode: base.CampusCode || undefined,
      };

      const pm = await axios.get(`${API_URL}/api/marks/analytics/pass-matrix`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      setPassMatrix(pm.data?.rows || []);
    } catch (e) {
      setPassMatrix([]);
      setErr(e?.response?.data?.message || "Failed to load pass matrix");
    } finally {
      setLoadingMatrix(false);
    }
  }, [token, base.CampusCode, base.UseLatest]);

  // -------------------- LOAD PANELS (Summary/Subjects/Grades/TopBottom + Latest AI) --------------------
  const loadPanelsForPivot = React.useCallback(
    async (effectiveParams) => {
      if (!token) return;

      setLoadingPanels(true);
      setErr("");
      try {
        const [s, sub, g, tb, latestAi] = await Promise.all([
          axios.get(`${API_URL}/api/marks/analytics/summary`, {
            headers: { Authorization: `Bearer ${token}` },
            params: effectiveParams,
          }),
          axios.get(`${API_URL}/api/marks/analytics/subjects`, {
            headers: { Authorization: `Bearer ${token}` },
            params: effectiveParams,
          }),
          axios.get(`${API_URL}/api/marks/analytics/grades`, {
            headers: { Authorization: `Bearer ${token}` },
            params: effectiveParams,
          }),
          axios.get(`${API_URL}/api/marks/analytics/top-bottom`, {
            headers: { Authorization: `Bearer ${token}` },
            params: effectiveParams,
          }),
          axios.get(`${API_URL}/api/marks/latest`, {
            headers: { Authorization: `Bearer ${token}` },
            params: effectiveParams,
          }),
        ]);

        setSummary(s.data || null);
        setSubjects(sub.data?.subjects || []);
        setGrades(g.data?.gradeDistribution || []);
        setTopBottom(tb.data || null);

        if (latestAi.data?.exists) {
          setAiGist(latestAi.data.gist || null);
          setAiMeta(latestAi.data.meta || null);
        } else {
          setAiGist(null);
          setAiMeta(null);
        }
      } catch (e) {
        setSummary(null);
        setSubjects([]);
        setGrades([]);
        setTopBottom(null);
        setAiGist(null);
        setAiMeta(null);
        setErr(e?.response?.data?.message || "Failed to load analytics panels");
      } finally {
        setLoadingPanels(false);
      }
    },
    [token]
  );

  const loadStudentsList = React.useCallback(async () => {
    if (!token) {
      setErr("Session expired. Please login again.");
      return;
    }

    setLoadingStudents(true);
    setErr("");
    try {
      const params = buildEffectiveParams(); // uses pivot filters
      const r = await axios.get(`${API_URL}/api/marks/analytics/students`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      setStudents(r.data?.students || []);
      setShowStudents(true);
    } catch (e) {
      setStudents([]);
      setShowStudents(true);
      setErr(e?.response?.data?.message || "Failed to load students list");
    } finally {
      setLoadingStudents(false);
    }
  }, [token, buildEffectiveParams]);

    // ✅ initial auto-load: matrix + panels for (All)
    React.useEffect(() => {
      if (!token) {
        setErr("Session expired. Please login again.");
        return;
      }
      loadPassMatrix();
      // panels for "All" (no pivot filters)
      //const effectiveParams = buildEffectiveParams();
      //loadPanelsForPivot(effectiveParams);
      loadPanelsForPivot({ CampusCode: base.CampusCode || undefined });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  // -------------------- AI GIST (uses current pivot filters) --------------------
  const handleGenerateAiGist = async () => {
    if (!token) {
      setErr("Session expired. Please login again.");
      return;
    }

    setLoadingAi(true);
    setErr("");
    try {
      const body = { ...buildEffectiveParams(), GenerationMode: "manual" };

      const r = await axios.post(`${API_URL}/api/marks/generate-gist`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setAiGist(r.data?.gist || null);

      // refresh latest record for the same pivot filters
      const latest = await axios.get(`${API_URL}/api/marks/latest`, {
        headers: { Authorization: `Bearer ${token}` },
        params: buildEffectiveParams(),
      });

      if (latest.data?.exists) {
        setAiGist(latest.data.gist || null);
        setAiMeta(latest.data.meta || null);
      } else {
        setAiGist(null);
        setAiMeta(null);
      }
    } catch (e) {
      setErr(e?.response?.data?.error || "AI gist failed");
    } finally {
      setLoadingAi(false);
    }
  };

  // -------------------- Risky Subjects --------------------
  const subjectChart = React.useMemo(() => {
    return (subjects || []).map((x) => {
      const appeared = x.appearedCount != null ? Number(x.appearedCount) : null;

      const passCount = x.passCount != null ? Number(x.passCount) : null;
      const failCount = x.failCount != null ? Number(x.failCount) : null;

      const passPercent =
        x.passPercent != null
          ? Number(x.passPercent)
          : appeared && passCount != null
          ? (passCount * 100) / appeared
          : null;

      const failPercent =
        x.failPercent != null
          ? Number(x.failPercent)
          : appeared && failCount != null
          ? (failCount * 100) / appeared
          : null;

      const absentPercent =
        x.studentCount && x.absentCount != null
          ? (Number(x.absentCount) * 100) / (Number(x.studentCount) || 1)
          : null;

      const withheldPercent =
        x.studentCount && x.withheldCount != null
          ? (Number(x.withheldCount) * 100) / (Number(x.studentCount) || 1)
          : null;

      const riskIndex = (failPercent || 0) + (absentPercent || 0) + (withheldPercent || 0);

      return {
        name: x.SubjectCode,
        subjectnamecode: x.SubjectNameCode,
        passCount,
        failCount,
        appearedCount: appeared,
        passPercent,
        failPercent,
        absentPercent,
        withheldPercent,
        riskIndex,
      };
    });
  }, [subjects]);

  const riskySubjects = React.useMemo(() => {
    return [...(subjectChart || [])]
      .filter((x) => x && x.riskIndex != null)
      .sort((a, b) => (b.riskIndex || 0) - (a.riskIndex || 0))
      .slice(0, 5);
  }, [subjectChart]);

  // -------------------- Pivot values & table build --------------------
  const pivotFieldValues = React.useMemo(() => {
    const map = {};
    for (const f of PIVOT_FIELDS) map[f.key] = new Set();
    (passMatrix || []).forEach((r) => {
      PIVOT_FIELDS.forEach((f) => map[f.key].add(getField(r, f.key)));
    });
    const out = {};
    for (const k in map) out[k] = [...map[k]].filter((x) => x !== "").sort((a, b) => a.localeCompare(b));
    return out;
  }, [passMatrix, PIVOT_FIELDS]);

  const CLIENT_SIDE_ONLY_KEYS = new Set([
    "AcademicYear",
    "SubCampusCode",
    "DegreeCode",
    "BranchCode",
    "Semester",
    "SubjectCode",
    "GradeCode",
  ]);

  const passMatrixFiltered = React.useMemo(() => {
    const active = pivotFiltersApplied || {};
    return (passMatrix || []).filter((r) => {
      for (const k of Object.keys(active)) {
        const selected = active[k] || [];
        if (!selected.length) continue;

        // ✅ only filter locally for keys present in passMatrix
        if (!CLIENT_SIDE_ONLY_KEYS.has(k)) continue;

        const v = getField(r, k);
        if (!selected.includes(v)) return false;
      }
      return true;
    });
  }, [passMatrix, pivotFiltersApplied]);

  const pivotResult = React.useMemo(() => {
    const rowKeys = pivotCfg.rows || [];
    const colKeys = pivotCfg.cols || [];
    const valueKey = pivotCfg.value;

    const rowKeyStr = (r) => rowKeys.map((k) => `${k}:${getField(r, k)}`).join("|");
    const colKeyStr = (r) => colKeys.map((k) => `${k}:${getField(r, k)}`).join("|");

    const parseParts = (kstr) =>
  (kstr || "")
    .split("|")
    .filter(Boolean)
    .map((p) => {
      const i = p.indexOf(":");
      const key = i >= 0 ? p.slice(0, i) : "";
      const val = i >= 0 ? p.slice(i + 1) : p;
      return { key, val };
    });

    const rowLabel = (kstr) => {
      const parts = parseParts(kstr);
      if (!parts.length) return "(All)";
      return parts.map(({ key, val }) => displayDimValue(key, val)).join(" • ");
    };

    const colLabel = (kstr) => {
      const parts = parseParts(kstr);
      if (!parts.length) return "(All)";
      return parts.map(({ key, val }) => displayDimValue(key, val)).join(" • ");
    };

      {/*
    const rowLabel = (kstr) =>
      kstr
        .split("|")
        .filter(Boolean)
        .map((p) => p.split(":")[1] || "")
        .join(" • ") || "(All)";

    const colLabel = (kstr) =>
      kstr
        .split("|")
        .filter(Boolean)
        .map((p) => p.split(":")[1] || "")
        .join(" • ") || "(All)";
        */}

    const cell = new Map();
    const rowSet = new Set();
    const colSet = new Set();

    (passMatrixFiltered || []).forEach((r) => {
      const rk = rowKeyStr(r);
      const ck = colKeyStr(r);
      rowSet.add(rk);
      colSet.add(ck);

      const key = `${rk}__${ck}`;
      if (!cell.has(key)) cell.set(key, { passSum: 0, appSum: 0, valSum: 0 });

      const acc = cell.get(key);
      const passC = Number(r.passCount || 0);
      const appC = Number(r.appearedCount || 0);

      acc.passSum += passC;
      acc.appSum += appC;

      if (valueKey === "passCount") acc.valSum += passC;
      else if (valueKey === "appearedCount") acc.valSum += appC;
    });

    const rowsSorted = [...rowSet].sort((a, b) => rowLabel(a).localeCompare(rowLabel(b)));
    const colsSorted = [...colSet].sort((a, b) => colLabel(a).localeCompare(colLabel(b)));

    const getCellDisplay = (rk, ck) => {
      const acc = cell.get(`${rk}__${ck}`);
      if (!acc) return null;

      if (valueKey === "passCount" || valueKey === "appearedCount") return acc.valSum;
      return acc.appSum > 0 ? (acc.passSum * 100) / acc.appSum : null;
    };

    const rowTotals = new Map();
    const colTotals = new Map();
    let grand = { passSum: 0, appSum: 0, valSum: 0 };

    for (const rk of rowsSorted) {
      for (const ck of colsSorted) {
        const acc = cell.get(`${rk}__${ck}`);
        if (!acc) continue;

        if (!rowTotals.has(rk)) rowTotals.set(rk, { passSum: 0, appSum: 0, valSum: 0 });
        if (!colTotals.has(ck)) colTotals.set(ck, { passSum: 0, appSum: 0, valSum: 0 });

        rowTotals.get(rk).passSum += acc.passSum;
        rowTotals.get(rk).appSum += acc.appSum;
        rowTotals.get(rk).valSum += acc.valSum;

        colTotals.get(ck).passSum += acc.passSum;
        colTotals.get(ck).appSum += acc.appSum;
        colTotals.get(ck).valSum += acc.valSum;

        grand.passSum += acc.passSum;
        grand.appSum += acc.appSum;
        grand.valSum += acc.valSum;
      }
    }

    const totalDisplay = (acc) => {
      if (!acc) return null;
      if (valueKey === "passCount" || valueKey === "appearedCount") return acc.valSum;
      return acc.appSum > 0 ? (acc.passSum * 100) / acc.appSum : null;
    };

    return {
      rowsSorted,
      colsSorted,
      rowLabel,
      colLabel,
      getCellDisplay,
      rowTotals,
      colTotals,
      grandTotal: totalDisplay(grand),
      totalDisplay,
      valueKey,
    };
  }, [passMatrixFiltered, pivotCfg, getField, displayDimValue]);

  const pivotMeasureLabel = (PIVOT_MEASURES.find((m) => m.key === pivotCfg.value)?.label) || "Value";
  const setArea = (areaKey, values) => setPivotCfg((p) => ({ ...p, [areaKey]: values }));

  // ---- Pivot Graph settings ----
  const [pivotGraphMaxSeries, setPivotGraphMaxSeries] = React.useState(8);
  const [pivotGraphMaxRows, setPivotGraphMaxRows] = React.useState(25);

  const formatPivotVal = (v) => {
    if (v == null) return "-";
    if (pivotResult.valueKey === "passPercent") return `${Number(v).toFixed(2)}%`;
    return Number(v).toLocaleString();
  };

  const pivotRowTotalsChart = React.useMemo(() => {
    const rows = (pivotResult.rowsSorted || []).slice(0, pivotGraphMaxRows);
    return rows.map((rk) => {
      const rt = pivotResult.rowTotals.get(rk);
      const v = pivotResult.totalDisplay(rt);
      return {
        name: pivotResult.rowLabel(rk) || "(Blank)",
        value: v == null ? null : Number(v),
      };
    });
  }, [pivotResult, pivotGraphMaxRows]);

  const pivotMultiSeriesChart = React.useMemo(() => {
    const cols = (pivotResult.colsSorted || []).slice(0, pivotGraphMaxSeries);
    const rows = (pivotResult.rowsSorted || []).slice(0, pivotGraphMaxRows);

    const series = cols.map((ck, i) => ({
      ck,
      key: `c${i}`,
      label: pivotResult.colLabel(ck) || "(Blank)",
    }));

    const data = rows.map((rk) => {
      const obj = { name: pivotResult.rowLabel(rk) || "(Blank)" };
      series.forEach((s) => {
        const v = pivotResult.getCellDisplay(rk, s.ck);
        obj[s.key] = v == null ? null : Number(v);
      });
      return obj;
    });

    return { series, data };
  }, [pivotResult, pivotGraphMaxSeries, pivotGraphMaxRows]);

const hashString = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // 32-bit
  }
  return Math.abs(hash);
};

// 24 distinct hue slots (15° steps). You can reduce to 12 if you want stronger separation.
const HUE_SLOTS = Array.from({ length: 24 }, (_, i) => i * 15);

// step must be coprime with HUE_SLOTS.length (24). 5, 7, 11, 13, 17, 19, 23 work.
const PROBE_STEP = 7;

/**
 * Build a deterministic label->color map for a given chart render.
 * Call this once per chart using the chart's series labels.
 */

const DISTINCT = [
  //"rgba(255, 184, 137, 1)","rgba(255, 138, 60, 0.92)", "rgba(255, 133, 52, 1)","rgba(255, 102, 0, 1)","rgba(185, 228, 255, 1)", "rgba(106, 198, 255, 1)", "rgba(22, 165, 254, 1)",
  "rgba(255, 197, 158, 1)","rgba(192, 231, 255, 1)",
  "rgba(254, 163, 103, 1)", "rgba(110, 198, 252, 1)",
  "rgba(255, 139, 61, 1)", "rgba(69, 181, 251, 1)",
  "rgba(255, 102, 0, 1)", "rgba(0, 157, 255, 1)",
];

const buildColorMap = (labels = []) => {
  const unique = Array.from(new Set(labels.filter(Boolean)));
  const map = {};
  unique.forEach((label, i) => {
    map[label] = DISTINCT[i % DISTINCT.length];
  });
  return map;
};

{/*
const buildColorMap = (labels = []) => {
  const unique = Array.from(new Set(labels.filter(Boolean)));

  const taken = new Array(HUE_SLOTS.length).fill(false);
  const map = {};

  unique.forEach((label, idx) => {
    let slot = hashString(label) % HUE_SLOTS.length;

    let tries = 0;
    while (taken[slot] && tries < HUE_SLOTS.length) {
      slot = (slot + PROBE_STEP) % HUE_SLOTS.length;
      tries++;
    }

    // if you have MORE series than HUE_SLOTS, repeats are inevitable
    if (tries >= HUE_SLOTS.length) {
      // fallback: still assign something
      slot = idx % HUE_SLOTS.length;
    }

    taken[slot] = true;

    const hue = HUE_SLOTS[slot];

    // keep lightness fixed for maximum distinctness (don’t vary it here)
    map[label] = `hsl(${hue}, 78%, 48%)`;
  });

  return map;
};
*/}

const seriesLabels = pivotMultiSeriesChart.series.map(s => s.label);
const colorMap = React.useMemo(() => buildColorMap(seriesLabels), [seriesLabels]);

const pivotSeriesLabels = React.useMemo(
  () => pivotMultiSeriesChart.series.map((s) => s.label),
  [pivotMultiSeriesChart.series]
);

const pivotColorMap = React.useMemo(
  () => buildColorMap(pivotSeriesLabels),
  [pivotSeriesLabels]
);

const pivotTotalsColorMap = React.useMemo(
  () => buildColorMap([pivotMeasureLabel]),
  [pivotMeasureLabel]
);

const tableWidth = 260 + (pivotResult.colsSorted.length * 160) + 160; // row label + each col + total

{/*
const hashString = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // force 32-bit
  }
  return Math.abs(hash);
};

const THEME_COLORS = [
  "#3d91e4", "#f16509", "#2e7d32", "#8e24aa",
  "#d81b60", "#00897b", "#fbc02d", "#6d4c41",
  "#546e7a", "#1e88e5", "#43a047", "#fb8c00",
];

const colorForLabel = (label) => {
  const h1 = hashString(label);
  const h2 = hashString(label + "_salt");
  return THEME_COLORS[(h1 + h2) % THEME_COLORS.length];
};
*/}
  // -------------------- RENDER --------------------
  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Student Marks Analytics
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              All panels update based on Pass % Pivot Filters
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {err ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}>
              {err}
            </Alert>
          ) : null}

          {/* Pivot Builder (ONLY controls now) */}
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Box sx={sectionInnerPadSx}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                  Filters
                </Typography>
              </Box>

              {loadingMatrix ? (
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading pass matrix…
                  </Typography>
                </Box>
              ) : !(passMatrix || []).length ? (
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  No pass matrix available.
                </Typography>
              ) : (
                <>
                <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
                  {/* Value */}
                  <Grid item xs={12} md={2.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Value</InputLabel>
                      <Select
                        label="Value"
                        value={pivotCfg.value}
                        onChange={(e) => setPivotCfg((p) => ({ ...p, value: e.target.value }))}
                        sx={inputSx}
                      >
                        {PIVOT_MEASURES.map((m) => (
                          <MenuItem key={m.key} value={m.key}>
                            {m.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                      
                  {/* Rows */}
                  <Grid item xs={12} md={2.5}>
                    <Autocomplete
                      multiple
                      options={PIVOT_FIELDS.map((x) => x.key)}
                      value={pivotCfg.rows}
                      onChange={(e, v) => setArea("rows", v)}
                      getOptionLabel={(k) => PIVOT_FIELDS.find((x) => x.key === k)?.label || k}
                      renderInput={(params) => <TextField {...params} label="Rows" size="small" sx={inputSx} />}
                    />
                  </Grid>
                      
                  {/* Columns */}
                  <Grid item xs={12} md={2.5}>
                    <Autocomplete
                      multiple
                      options={PIVOT_FIELDS.map((x) => x.key)}
                      value={pivotCfg.cols}
                      onChange={(e, v) => setArea("cols", v)}
                      getOptionLabel={(k) => PIVOT_FIELDS.find((x) => x.key === k)?.label || k}
                      renderInput={(params) => <TextField {...params} label="Columns" size="small" sx={inputSx} />}
                    />
                  </Grid>
                      
                  {/* Actions */}
                  <Grid item xs={12} md={4.5}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: { xs: "flex-start", md: "flex-end" },
                        flexWrap: "wrap",
                      }}
                    >
                      <Button
                        variant="contained"
                        sx={primaryBtnSx}
                        onClick={() => {
                          // apply draft -> applied
                          setPivotFiltersApplied(pivotFiltersDraft);
                        
                          // load panels using applied
                          const effectiveParams = {
                            CampusCode: base.CampusCode || undefined,
                            ...(() => {
                              const out = {};
                              const pf = pivotFiltersDraft || {};
                              Object.keys(pf).forEach((k) => {
                                const arr = (pf[k] || []).filter(Boolean);
                                if (!arr.length) return;
                                out[k] = arr.length === 1 ? arr[0] : arr;
                              });
                              return out;
                            })(),
                          };
                        
                          loadPanelsForPivot(effectiveParams);
                        }}
                      >
                        Apply Filter
                      </Button>
                      
                      <Button
                        variant="outlined"
                        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
                        onClick={() => {
                          const empty = {
                            AcademicYear: [],
                            SubCampusCode: [],
                            DegreeCode: [],
                            BranchCode: [],
                            Semester: [],
                            SubjectCode: [],
                            GradeCode: [],
                            NoOfBacklogs: [],
                            PercentageBucket: [],
                            CgpaBucket: [],
                          };
                          setPivotFiltersDraft(empty);
                          setPivotFiltersApplied(empty);
                          loadPanelsForPivot({ CampusCode: base.CampusCode || undefined });
                        }}
                      >
                        Clear Filter
                      </Button>
                      
                      <Button
                        variant="outlined"
                        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
                        onClick={() => {
                          const empty = {
                            AcademicYear: [],
                            SubCampusCode: [],
                            DegreeCode: [],
                            BranchCode: [],
                            Semester: [],
                            SubjectCode: [],
                            GradeCode: [],
                            NoOfBacklogs: [],
                            PercentageBucket: [],
                            CgpaBucket: [],
                          };
                        
                          setPivotCfg({
                            rows: ["SubCampusCode"],
                            cols: ["Semester"],
                            filters: ["AcademicYear", "SubCampusCode", "DegreeCode", "BranchCode", "Semester", "SubjectCode", "GradeCode" , "NoOfBacklogs", "PercentageBucket", "CgpaBucket"],
                            value: "passPercent",
                          });
                        
                          setPivotFiltersDraft(empty);
                          setPivotFiltersApplied(empty);
                        
                          loadPanelsForPivot({ CampusCode: base.CampusCode || undefined });
                        }}
                      >
                        Reset Pivot
                      </Button>
                      
                      <Button
                        variant="contained"
                        sx={primaryBtnSx}
                        onClick={() => setPivotCfg((p) => ({ ...p, rows: p.cols, cols: p.rows }))}
                      >
                        Swap
                      </Button>
                    </Box>
                  </Grid>
                      
                  {/* Filter Fields + Students buttons row */}
                  <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={8}>
                        <Autocomplete
                          multiple
                          options={PIVOT_FIELDS.map((x) => x.key)}
                          value={pivotCfg.filters}
                          onChange={(e, v) => setPivotCfg((p) => ({ ...p, filters: v }))}
                          getOptionLabel={(k) => PIVOT_FIELDS.find((x) => x.key === k)?.label || k}
                          renderInput={(params) => (
                            <TextField {...params} label="Filter Fields (show filters below)" size="small" sx={inputSx} />
                          )}
                        />
                      </Grid>
                        
                      <Grid item xs={12} md={4}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: { xs: "flex-start", md: "flex-end" },
                            flexWrap: "wrap",
                          }}
                        >
                          <Button variant="contained" sx={primaryBtnSx} onClick={loadStudentsList} disabled={loadingStudents}>
                            {loadingStudents ? "Loading…" : "Generate Students List"}
                          </Button>

                          <Button
                            variant="outlined"
                            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
                            onClick={() => {
                              setStudents([]);
                              setShowStudents(false);
                            }}
                          >
                            Clear Students
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                
                  {!!(pivotCfg.filters || []).length && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {(pivotCfg.filters || []).map((fk) => (
                        <Grid item xs={12} md={2.4} key={fk}>
                          <Autocomplete
                            multiple
                            options={BUCKET_OPTIONS[fk] || pivotFieldValues[fk] || []}            // still strings (SubjectCode)
                            value={pivotFiltersDraft[fk] || []}             // still strings
                            onChange={(e, v) => setPivotFiltersDraft((p) => ({ ...p, [fk]: v }))}
                            getOptionLabel={(opt) => {
                              // opt is a string (SubjectCode)
                              return fk === "SubjectCode" ? formatSubjectOption(opt) : String(opt ?? "");
                            }}
                            renderOption={(props, option) => (
                              <li {...props}>
                                {fk === "SubjectCode" ? formatSubjectOption(option) : String(option ?? "")}
                              </li>
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  {...getTagProps({ index })}
                                  key={`${fk}-${option}-${index}`}
                                  label={fk === "SubjectCode" ? formatSubjectOption(option) : String(option ?? "")}
                                  size="small"
                                  sx={{ ...chipSx, mr: 0.5, mb: 0.5 }}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={`Filter: ${PIVOT_FIELDS.find((x) => x.key === fk)?.label || fk}`}
                                size="small"
                                sx={inputSx}
                              />
                            )}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  
                </>
              )}
            </Box>
          </Paper>

          {/* Summary (pivot-driven) */}
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Box sx={sectionInnerPadSx}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>Summary</Typography>
                {loadingPanels ? <CircularProgress size={16} /> : null}
              </Box>

              {!summary && loadingPanels ? (
                <Grid container spacing={2}>
                  {[1, 2, 3, 4].map((k) => (
                    <Grid key={k} item xs={12} md={3}>
                      <Skeleton variant="rounded" height={86} />
                    </Grid>
                  ))}
                </Grid>
              ) : !summary ? (
                <Typography color="text.secondary">Select pivot filters to see summary.</Typography>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={2.4}>
                    <Card elevation={0} sx={kpiCardSx}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Total Students
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: THEME_ORANGE, mt: 0.5 }}>
                          {summary.totalStudents ?? 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={2.4}>
                    <Card elevation={0} sx={kpiCardSx}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Total Subjects
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: THEME_ORANGE, mt: 0.5 }}>
                          {summary.totalSubjects ?? 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={2.4}>
                    <Card elevation={0} sx={kpiCardSx}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Avg Credit
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: THEME_ORANGE, mt: 0.5 }}>
                          {summary.avgCredit == null ? "-" : Number(summary.avgCredit).toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={2.4}>
                    <Card elevation={0} sx={kpiCardSx}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Avg External
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: THEME_ORANGE, mt: 0.5 }}>
                          {summary.avgExternal == null ? "-" : Number(summary.avgExternal).toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={2.4}>
                    <Card elevation={0} sx={kpiCardSx}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Pass / Fail
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, mt: 0.5 }}>
                          Pass: {summary.passCount ?? 0} • Fail: {summary.failCount ?? 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Paper>

          {/* AI Gist (pivot-driven latest + generate uses pivot filters) */}
          {/*Commented AI Gist for now*/}
          {/* 
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Box sx={sectionInnerPadSx}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>AI Gist</Typography>

                  <Button
                    variant="contained"
                    startIcon={loadingAi ? <CircularProgress size={18} sx={{ color: "white" }} /> : <AutoAwesomeIcon />}
                    onClick={handleGenerateAiGist}
                    disabled={loadingAi}
                    sx={primaryBtnSx}
                  >
                    {loadingAi ? "Generating…" : "Generate AI Gist"}
                  </Button>
                </Box>

                {aiMeta?.GeneratedAt ? (
                  <Typography variant="caption" color="text.secondary">
                    Last generated: {new Date(aiMeta.GeneratedAt).toLocaleString()} • {aiMeta.GenerationMode}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No AI summary saved for this pivot filter yet.
                  </Typography>
                )}
              </Box>

              {!aiGist ? (
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  Click “Generate AI Gist” to create an executive summary and action plan (for current pivot filters).
                </Typography>
              ) : (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ fontWeight: 800, mb: 1 }}>{aiGist.gist}</Typography>

                  {!!(aiGist.positives || []).length && (
                    <>
                      <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mt: 1 }}>Positives</Typography>
                      {(aiGist.positives || []).map((x, i) => (
                        <Typography key={i} variant="body2">
                          • {x}
                        </Typography>
                      ))}
                    </>
                  )}

                  {!!(aiGist.concerns || []).length && (
                    <>
                      <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mt: 1 }}>Concerns</Typography>
                      {(aiGist.concerns || []).map((x, i) => (
                        <Typography key={i} variant="body2">
                          • {x}
                        </Typography>
                      ))}
                    </>
                  )}

                  {!!(aiGist.actionable_recommendations || []).length && (
                    <>
                      <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mt: 1 }}>Action Plan</Typography>
                      {(aiGist.actionable_recommendations || []).map((x, i) => (
                        <Typography key={i} variant="body2">
                          {i + 1}. {x}
                        </Typography>
                      ))}
                    </>
                  )}

                  {aiGist.confidence_note ? (
                    <Alert severity="info" sx={{ mt: 1, borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}>
                      {aiGist.confidence_note}
                    </Alert>
                  ) : null}
                </Box>
              )}
            </Box>
          </Paper>
            */}
          {/* Pass % Pivot (Drag-like Builder) */}
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Box sx={sectionInnerPadSx}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>Pass % Pivot (Drag-like Builder)</Typography>
                {loadingPanels ? <CircularProgress size={16} /> : null}
              </Box>
                {!pivotResult.rowsSorted.length || !pivotResult.colsSorted.length ? (
                    <Typography color="text.secondary">No rows after pivot filters.</Typography>
                  ) : (
                    <Box sx={{
                              overflowX: "auto",
                              overflowY: "hidden",
                              WebkitOverflowScrolling: "touch",
                              touchAction: "pan-x",
                              width: "100%",
                              maxWidth: "100%",
                            }}>
                     {/* <Box sx={{ minWidth: 900, border: `1px solid ${BORDER}`, borderRadius: 2, overflow: "visible" }}> */}
                       

<Box
  sx={{
    width: "max-content",
    minWidth: Math.max(900, tableWidth),
    border: `1px solid ${BORDER}`,
    borderRadius: 2,
    overflow: "hidden", // keep rounded corners clean
    backgroundColor: "#fff",
  }}
>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: `260px repeat(${pivotResult.colsSorted.length}, 160px) 160px`,
                            background: "rgba(25,118,210,0.10)",
                          }}
                        >
                          <Box sx={{ p: 1, fontWeight: 900 }}>
                            Rows:{" "}
                            {(pivotCfg.rows || [])
                              .map((k) => PIVOT_FIELDS.find((x) => x.key === k)?.label || k)
                              .join(" • ") || "(All)"}
                          </Box>

                          {pivotResult.colsSorted.map((ck) => (  
                            <Box key={ck} sx={{ p: 1, 
                              fontWeight: 900, 
                              textAlign: "right", 
                              whiteSpace: "nowrap", 
                              overflow: "hidden", 
                              textOverflow: "ellipsis",}}
                              title={pivotResult.colLabel(ck) || "(Blank)"} 
                             >
                              {pivotResult.colLabel(ck) || "(Blank)"}
                            </Box>
                          ))}

                          <Box sx={{ p: 1, fontWeight: 900, textAlign: "right" }}>
                            Total ({pivotMeasureLabel})
                          </Box>
                        </Box>

                        {pivotResult.rowsSorted.map((rk, rIdx) => (
                          <Box
                            key={rk}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: `260px repeat(${pivotResult.colsSorted.length}, 160px) 160px`,
                              background: rIdx % 2 ? "rgba(25,118,210,0.04)" : "#fff",
                              borderTop: `1px solid rgba(25,118,210,0.10)`,
                            }}
                          >
                            <Box sx={{ p: 1, fontWeight: 900 }}>{pivotResult.rowLabel(rk) || "(Blank)"}</Box>

                            {pivotResult.colsSorted.map((ck) => {
                              const v = pivotResult.getCellDisplay(rk, ck);
                              const txt =
                                v == null
                                  ? "-"
                                  : pivotResult.valueKey === "passPercent"
                                  ? `${Number(v).toFixed(2)}%`
                                  : `${Number(v).toLocaleString()}`;
                              return (
                                <Box key={`${rk}__${ck}`} sx={{ p: 1, textAlign: "right", fontWeight: 800 }}>
                                  {txt}
                                </Box>
                              );
                            })}

                            {(() => {
                              const rt = pivotResult.rowTotals.get(rk);
                              const v = pivotResult.totalDisplay(rt);
                              const txt =
                                v == null
                                  ? "-"
                                  : pivotResult.valueKey === "passPercent"
                                  ? `${Number(v).toFixed(2)}%`
                                  : `${Number(v).toLocaleString()}`;
                              return (
                                <Box sx={{ p: 1, textAlign: "right", fontWeight: 900 }}>
                                  {txt}
                                </Box>
                              );
                            })()}
                          </Box>
                        ))}

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: `260px repeat(${pivotResult.colsSorted.length}, 160px) 160px`,
                            background: "rgba(240,120,40,0.10)",
                            borderTop: `1px solid rgba(25,118,210,0.12)`,
                          }}
                        >
                          <Box sx={{ p: 1, fontWeight: 900 }}>Grand Total</Box>

                          {pivotResult.colsSorted.map((ck) => {
                            const ct = pivotResult.colTotals.get(ck);
                            const v = pivotResult.totalDisplay(ct);
                            const txt =
                              v == null
                                ? "-"
                                : pivotResult.valueKey === "passPercent"
                                ? `${Number(v).toFixed(2)}%`
                                : `${Number(v).toLocaleString()}`;
                            return (
                              <Box key={`CT__${ck}`} sx={{ p: 1, textAlign: "right", fontWeight: 900 }}>
                                {txt}
                              </Box>
                            );
                          })}

                          <Box sx={{ p: 1, textAlign: "right", fontWeight: 900 }}>
                            {pivotResult.grandTotal == null
                              ? "-"
                              : pivotResult.valueKey === "passPercent"
                              ? `${Number(pivotResult.grandTotal).toFixed(2)}%`
                              : `${Number(pivotResult.grandTotal).toLocaleString()}`}
                          </Box>
                        </Box>
                      </Box>

                      {/* Pivot Graph */}
                      <Box sx={{ mt: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            flexWrap: "wrap",
                            mb: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                            Pivot Graph (Live)
                          </Typography>

                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                            <TextField
                              size="small"
                              type="number"
                              label="Max Series (Cols)"
                              value={pivotGraphMaxSeries}
                              onChange={(e) => setPivotGraphMaxSeries(Math.max(1, Number(e.target.value || 1)))}
                              sx={{ width: 160, ...inputSx }}
                            />
                            <TextField
                              size="small"
                              type="number"
                              label="Max Rows"
                              value={pivotGraphMaxRows}
                              onChange={(e) => setPivotGraphMaxRows(Math.max(1, Number(e.target.value || 1)))}
                              sx={{ width: 140, ...inputSx }}
                            />
                          </Box>
                        </Box>

                        {pivotResult.colsSorted.length > pivotGraphMaxSeries ? (
                          <>
                            <Alert severity="info" sx={{ mb: 1, borderRadius: 2 }}>
                              Too many pivot columns ({pivotResult.colsSorted.length}). Showing Row Totals graph.
                              Increase “Max Series” to see multi-series chart.
                            </Alert>

                            <Box sx={{ height: Math.max(320, pivotRowTotalsChart.length * 36) }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={pivotRowTotalsChart}
                                  layout="vertical"
                                  margin={{ top: 10, right: 20, bottom: 10, left: 0 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis type="number" domain={pivotResult.valueKey === "passPercent" ? [0, 100] : ["auto", "auto"]} />
                                  <YAxis type="category" dataKey="name" width={320} />
                                  <Tooltip formatter={(val) => [formatPivotVal(val), `Total (${pivotMeasureLabel})`]} />
                                  
                                  <Bar dataKey="value" fill={pivotTotalsColorMap[pivotMeasureLabel]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                          </>
                        ) : (
                          <Box sx={{ height: Math.max(360, (pivotMultiSeriesChart.data?.length || 0) * 42) }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={pivotMultiSeriesChart.data}
                                layout="vertical"
                                margin={{ top: 10, right: 20, bottom: 10, left: 0 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" domain={pivotResult.valueKey === "passPercent" ? [0, 100] : ["auto", "auto"]} />
                                <YAxis type="category" dataKey="name" width={320} />
                                <Legend />
                                <Tooltip
                                  formatter={(val, key) => {
                                    const s = pivotMultiSeriesChart.series.find((x) => x.key === key);
                                    return [formatPivotVal(val), s?.label || key];
                                  }}
                                />
                                {pivotMultiSeriesChart.series.map((s) => (
                                  <Bar key={s.key} dataKey={s.key} name={s.label} fill={pivotColorMap[s.label]} />
                                ))}
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        )}

                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          Graph updates live with Rows/Columns/Value and Pivot Filters. Pass % uses weighted formula (ΣPass / ΣAppeared).
                        </Typography>
                      </Box>
                    </Box>
                  )}

          {showStudents && (
            <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
              <Box sx={sectionInnerPadSx}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                    Students ({students.length})
                  </Typography>
                  {loadingStudents ? <CircularProgress size={16} /> : null}
                </Box>

                {!students.length ? (
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    No students found for the selected filters.
                  </Typography>
                ) : (
                  <Box sx={{ mt: 1, maxHeight: 360, overflow: "auto", border: `1px solid ${BORDER}`, borderRadius: 2 }}>
                    {(students || []).map((s, idx) => (
                      <Box
                        key={`${s.StudentID}-${idx}`}
                        sx={{
                          px: 1.5,
                          py: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                          borderBottom: `1px solid rgba(25,118,210,0.10)`,
                          background: idx % 2 ? "rgba(25,118,210,0.04)" : "#fff",
                        }}
                      >
                        <Typography sx={{ fontWeight: 900 }}>{s.StudentID}</Typography>
                        <Typography color="text.secondary" sx={{ fontWeight: 700 }}>
                          {s.FullName || "-"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Paper>
          )}
          </Box>
          </Paper>

          {/* Risky Subjects (pivot-driven) */}
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Box sx={sectionInnerPadSx}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                  Risky Subjects (Fail% + Absent% + Withheld%)
                </Typography>
                {loadingPanels ? <CircularProgress size={16} /> : null}
              </Box>

              {!(riskySubjects || []).length ? (
                <Typography color="text.secondary">Select pivot filters to see risky subjects.</Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {riskySubjects.map((s) => (
                    <Chip
                      key={s.name}
                      sx={{ ...chipSx, mb: 1 }}
                      label={`${s.subjectnamecode}..${s.name} — Risk ${Number(s.riskIndex || 0).toFixed(1)} (Pass ${
                        s.passPercent == null ? "-" : `${Number(s.passPercent).toFixed(1)}%`
                      })`}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Paper>

          {/* Grade distribution + Top/Bottom (pivot-driven) */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={sectionSx}>
                <Box sx={sectionInnerPadSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>Grade Distribution</Typography>
                    {loadingPanels ? <CircularProgress size={16} /> : null}
                  </Box>

                  {(grades || []).length ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {grades.map((g, i) => (
                        <Chip key={i} label={`${g.Grade}: ${g.Count}`} sx={{ ...chipSx, mb: 1 }} />
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">No grade data.</Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={sectionSx}>
                <Box sx={sectionInnerPadSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>Top / Bottom Performers</Typography>
                    {loadingPanels ? <CircularProgress size={16} /> : null}
                  </Box>

                  {!topBottom ? (
                    <Typography color="text.secondary">No data.</Typography>
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        Mode: {topBottom.mode}
                      </Typography>

                      <Divider sx={{ my: 1 }} />

                      <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>Top</Typography>
                      {(topBottom.top || []).slice(0, 5).map((x, i) => (
                        <Typography key={i} variant="body2">
                          • {x.StudentID}{" "}
                          {x.GPA != null
                            ? `— GPA ${Number(x.GPA).toFixed(2)}`
                            : x.CGPA != null
                            ? `— CGPA ${Number(x.CGPA).toFixed(2)}`
                            : ""}
                        </Typography>
                      ))}

                      <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mt: 1 }}>Bottom</Typography>
                      {(topBottom.bottom || []).slice(0, 5).map((x, i) => (
                        <Typography key={i} variant="body2">
                          • {x.StudentID}{" "}
                          {x.GPA != null
                            ? `— GPA ${Number(x.GPA).toFixed(2)}`
                            : x.CGPA != null
                            ? `— CGPA ${Number(x.CGPA).toFixed(2)}`
                            : ""}
                        </Typography>
                      ))}
                    </>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* tiny status */}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            Panels refresh automatically when Pivot Filters change.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}